import { CalendarDate, CalendarDateTime, getLocalTimeZone, today } from '@internationalized/date';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DateValue } from 'react-aria-components';
import { toCalendarDate } from './utils/dateUtils';
import { formatDateValue, formatTimeValue } from './utils/formatters';
import { parseInputText, parseTimeText } from './utils/parsers';
import { clampDateToRange, clampTime, currentTimeClampedToRange } from './utils/timeUtils';

// ─── Options & Return Types ───────────────────────────────────────────────────

export interface DatePickerControllerOptions {
  value: DateValue | null | undefined;
  onChange?: (value: DateValue | null) => void;
  onFocusChange?: (isFocused: boolean) => void;
  granularity: 'day' | 'hour' | 'minute' | 'second';
  hourCycle: 12 | 24;
  minValue?: DateValue;
  maxValue?: DateValue;
  isDisabled?: boolean;
}

export interface DatePickerController {
  // Refs — passed directly to JSX elements
  containerRef: React.RefObject<HTMLDivElement | null>;
  dateInputRef: React.RefObject<HTMLInputElement | null>;
  timeInputRef: React.RefObject<HTMLInputElement | null>;
  /** Renamed from calendarRef — attached to whichever popover is currently mounted */
  popoverRef: React.RefObject<HTMLDivElement | null>;

  // State — read-only for rendering decisions
  dateText: string;
  timeText: string;
  isFocused: boolean;
  popoverOpen: boolean;
  popoverMode: 'calendar' | 'time';
  internalError: string | null;
  hasTime: boolean;

  // Derived values — computed inside hook
  calendarValue: CalendarDate;
  timePickerValue: { hour: number; minute: number; second: number };
  timeConstraints: {
    minTime?: { hour: number; minute: number; second: number };
    maxTime?: { hour: number; minute: number; second: number };
  };

  // Event handlers
  handleContainerFocus: (e: React.FocusEvent) => void;
  handleContainerBlur: () => void;
  handleCalendarSelect: (date: CalendarDate) => void;
  handleTimePickerChange: (hour: number, minute: number, second: number) => void;
  closePopover: () => void;
  commit: () => void;

  // Callbacks for child inputs
  onDateTextChange: (v: string) => void;
  onTimeTextChange: (v: string) => void;
  onDateCommit: () => void;
  onTimeCommit: () => void;
  onAdvanceToTime: (() => void) | undefined;
  onRetreatToDate: () => void;
  onEnterSegmentMode: (fn: () => void) => void;
  onOpenTimePicker: () => void;
  seedTime: (forDate?: CalendarDate) => string;
  onArrowKeyInDate: (e: React.KeyboardEvent) => void;
  onEnterInDate: () => void;
  onContainerKeyDown: (e: React.KeyboardEvent) => void;
  onWrapperClick: (e: React.MouseEvent) => void;
  onCalendarIconClick: () => void;
  onClockIconClick: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useDatePickerController({
  value,
  onChange,
  onFocusChange,
  granularity,
  hourCycle,
  minValue,
  maxValue,
  isDisabled,
}: DatePickerControllerOptions): DatePickerController {
  const containerRef = useRef<HTMLDivElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const justClosedRef = useRef(false);
  const enterSegmentModeRef = useRef<(() => void) | null>(null);
  // Seed values set synchronously before commit() on Tab.
  // IMPORTANT: pendingSeedRef must always be written before commit() is called and
  // read at the very start of commit(). This ordering is load-bearing — do not make async.
  const pendingSeedRef = useRef<{ date?: string; time?: string }>({});

  const hasTime = granularity !== 'day';

  // ── Seed helpers ─────────────────────────────────────────────────────────

  const seedDate = useCallback((): string => {
    const t = today(getLocalTimeZone());
    const minCal = minValue ? toCalendarDate(minValue) : null;
    const maxCal = maxValue ? toCalendarDate(maxValue) : null;
    return formatDateValue(clampDateToRange(t, minCal, maxCal));
  }, [minValue, maxValue]);

  const seedTime = useCallback(
    (forDate?: CalendarDate): string => {
      const { hour: h24, minute: m, second: s } = currentTimeClampedToRange(forDate, minValue, maxValue);
      const h = hourCycle === 12 ? h24 % 12 || 12 : h24;
      const period = hourCycle === 12 ? (h24 >= 12 ? ' PM' : ' AM') : '';
      let base = String(h).padStart(2, '0');

      if (granularity === 'second') {
        base = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      } else if (granularity === 'minute') {
        base = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }

      return base + period;
    },
    [hourCycle, granularity, minValue, maxValue]
  );

  // ── State ────────────────────────────────────────────────────────────────

  const [dateText, setDateText] = useState(() => formatDateValue(value));
  const [timeText, setTimeText] = useState(() => (hasTime ? formatTimeValue(value, granularity, hourCycle) : ''));
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverMode, setPopoverMode] = useState<'calendar' | 'time'>('calendar');
  const [isFocused, setIsFocused] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  // Sync external value → text when not focused (skip if user has an invalid input)
  useEffect(() => {
    if (!isFocused && !internalError) {
      setDateText(formatDateValue(value));
      if (hasTime) setTimeText(formatTimeValue(value, granularity, hourCycle));
    }
  }, [value, isFocused, internalError, hasTime, granularity, hourCycle]);

  // ── Commit logic ─────────────────────────────────────────────────────────

  const commit = useCallback(() => {
    const dt = pendingSeedRef.current.date ?? dateText;
    const tt = pendingSeedRef.current.time ?? timeText;
    pendingSeedRef.current = {};

    if (dt.trim() !== '' && dateText.trim() === '') setDateText(dt);
    if (tt.trim() !== '' && timeText.trim() === '') setTimeText(tt);

    // Empty → clear value
    if (dt.trim() === '' && (!hasTime || tt.trim() === '')) {
      setInternalError(null);
      if (value != null) onChange?.(null);
      return;
    }

    const parsedDate = parseInputText(dt);
    if (!parsedDate) {
      setInternalError('Invalid date');
      return;
    }

    if (minValue && parsedDate.compare(toCalendarDate(minValue)) < 0) {
      setInternalError('Date is before the minimum allowed date');
      return;
    }
    if (maxValue && parsedDate.compare(toCalendarDate(maxValue)) > 0) {
      setInternalError('Date is after the maximum allowed date');
      return;
    }

    setDateText(formatDateValue(parsedDate));

    if (hasTime) {
      const parsedTime = parseTimeText(tt, granularity);
      if (!parsedTime) {
        setInternalError('Invalid time');
        return;
      }
      const time = clampTime(parsedDate, parsedTime.hour, parsedTime.minute, parsedTime.second, minValue, maxValue);
      if (time.hour !== parsedTime.hour || time.minute !== parsedTime.minute || time.second !== parsedTime.second) {
        setInternalError('Time is outside the allowed range');
        return;
      }
      setInternalError(null);
      setTimeText(
        formatTimeValue(
          new CalendarDateTime(parsedDate.year, parsedDate.month, parsedDate.day, time.hour, time.minute, time.second),
          granularity,
          hourCycle
        )
      );
      const newDt = new CalendarDateTime(
        parsedDate.year,
        parsedDate.month,
        parsedDate.day,
        time.hour,
        time.minute,
        time.second
      );
      const cur = value && 'hour' in value ? (value as CalendarDateTime) : null;
      const unchanged =
        cur &&
        cur.year === newDt.year &&
        cur.month === newDt.month &&
        cur.day === newDt.day &&
        cur.hour === newDt.hour &&
        cur.minute === newDt.minute &&
        (granularity === 'minute' || cur.second === newDt.second);
      if (!unchanged) onChange?.(newDt);
    } else {
      setInternalError(null);
      const cur = value;
      const unchanged =
        cur && cur.year === parsedDate.year && cur.month === parsedDate.month && cur.day === parsedDate.day;
      if (!unchanged) onChange?.(parsedDate);
    }
  }, [dateText, timeText, hasTime, granularity, hourCycle, onChange, value, minValue, maxValue]);

  // Re-validate when min/max change. `commit` and `isFocused` are intentionally omitted from the
  // dep array: this effect should only fire when bounds change, not on every keystroke or focus
  // toggle. `commit` closes over the latest text state via useCallback so it is always current.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — see comment above
  useEffect(() => {
    if (!isFocused && !isDisabled) commit();
  }, [minValue, maxValue, isDisabled]);

  // ── Popover open/close ───────────────────────────────────────────────────

  const closePopover = useCallback(() => {
    justClosedRef.current = true;
    setPopoverOpen(false);
    justClosedTimeout.current = setTimeout(() => {
      justClosedRef.current = false;
    }, 100);
  }, []);

  useEffect(() => {
    if (!popoverOpen) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      closePopover();
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [popoverOpen, closePopover]);

  // ── Focus management ─────────────────────────────────────────────────────

  const blurTimeout = useRef<ReturnType<typeof setTimeout>>();
  const justClosedTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Clear both timeouts on unmount to prevent setState on a dead component.
  useEffect(() => {
    return () => {
      clearTimeout(blurTimeout.current);
      clearTimeout(justClosedTimeout.current);
    };
  }, []);

  const handleContainerFocus = (e: React.FocusEvent) => {
    clearTimeout(blurTimeout.current);
    if (!isFocused) {
      setIsFocused(true);
      onFocusChange?.(true);
    }
    // Only auto-open when an actual text input is focused, not the icon buttons
    const isDateFocus = dateInputRef.current?.contains(e.target as Node);
    const isTimeFocus = timeInputRef.current?.contains(e.target as Node);
    if ((isDateFocus || isTimeFocus) && !justClosedRef.current) {
      setPopoverMode(isTimeFocus && hasTime ? 'time' : 'calendar');
      setPopoverOpen(true);
    }
  };

  const handleContainerBlur = () => {
    blurTimeout.current = setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsFocused(false);
        onFocusChange?.(false);
        commit();
        setPopoverOpen(false);
      }
    }, 0);
  };

  // ── Calendar selection ───────────────────────────────────────────────────

  const handleCalendarSelect = (date: CalendarDate) => {
    setDateText(formatDateValue(date));

    if (hasTime) {
      const parsedTime = parseTimeText(timeText, granularity);
      const now = new Date();
      const raw = parsedTime ?? { hour: now.getHours(), minute: now.getMinutes(), second: now.getSeconds() };
      const time = clampTime(date, raw.hour, raw.minute, raw.second, minValue, maxValue);
      onChange?.(new CalendarDateTime(date.year, date.month, date.day, time.hour, time.minute, time.second));
    } else {
      onChange?.(date);
    }

    closePopover();
    dateInputRef.current?.focus();
  };

  // ── Time picker selection ────────────────────────────────────────────────

  const handleTimePickerChange = (hour: number, minute: number, second: number) => {
    const parsedDate = parseInputText(dateText);
    const d = parsedDate ?? (value ? toCalendarDate(value) : null);
    if (!d) return;
    const newDt = new CalendarDateTime(d.year, d.month, d.day, hour, minute, second);
    const newText = formatTimeValue(newDt, granularity, hourCycle);
    setTimeText(newText);

    // Focus time input and select the segment matching what the user just changed.
    // We do this in a rAF so the input value has updated before we measure.
    requestAnimationFrame(() => {
      const el = timeInputRef.current;
      if (!el) return;
      el.focus();
      // Find which column was most recently changed by locating the changed segment.
      // Segments are split by ':' and space — select the first numeric segment by default.
      const numericPart = newText.match(/^[\d:]+/)?.[0] ?? '';
      const parts = numericPart.split(':');
      // Determine which segment index changed (hour=0, minute=1, second=2)
      const oldParsed = parseTimeText(timeText, granularity);
      let changedSeg = 0;
      if (oldParsed) {
        if (oldParsed.second !== second) changedSeg = 2;
        else if (oldParsed.minute !== minute) changedSeg = 1;
        else changedSeg = 0;
      }
      const segIdx = Math.min(changedSeg, parts.length - 1);
      const start = parts.slice(0, segIdx).reduce((acc, p) => acc + p.length + 1, 0);
      el.setSelectionRange(start, start + parts[segIdx].length);
      enterSegmentModeRef.current?.();
    });

    // Check if this time is out of range — show error but don't commit
    const clamped = clampTime(d, hour, minute, second, minValue, maxValue);
    if (clamped.hour !== hour || clamped.minute !== minute || clamped.second !== second) {
      setInternalError('Time is outside the allowed range');
      return;
    }
    setInternalError(null);
    onChange?.(newDt);
  };

  // ── Derived values ───────────────────────────────────────────────────────

  const calendarValue = (() => {
    const parsed = parseInputText(dateText);
    if (parsed) return parsed;
    if (value) return toCalendarDate(value);
    const t = today(getLocalTimeZone());
    if (minValue && t.compare(toCalendarDate(minValue)) < 0) return toCalendarDate(minValue);
    if (maxValue && t.compare(toCalendarDate(maxValue)) > 0) return toCalendarDate(maxValue);
    return t;
  })();

  const timeConstraints = calendarValue
    ? (() => {
        const minDt = minValue && 'hour' in minValue ? (minValue as CalendarDateTime) : null;
        const maxDt = maxValue && 'hour' in maxValue ? (maxValue as CalendarDateTime) : null;
        const isMinDay = minDt && calendarValue.compare(toCalendarDate(minDt)) === 0;
        const isMaxDay = maxDt && calendarValue.compare(toCalendarDate(maxDt)) === 0;
        return {
          minTime: isMinDay ? { hour: minDt!.hour, minute: minDt!.minute, second: minDt!.second } : undefined,
          maxTime: isMaxDay ? { hour: maxDt!.hour, minute: maxDt!.minute, second: maxDt!.second } : undefined,
        };
      })()
    : { minTime: undefined, maxTime: undefined };

  const timePickerValue = (() => {
    const parsedTime = parseTimeText(timeText, granularity);
    if (parsedTime) return parsedTime;
    if (value && 'hour' in value)
      return {
        hour: (value as CalendarDateTime).hour,
        minute: (value as CalendarDateTime).minute,
        second: (value as CalendarDateTime).second,
      };
    const now = new Date();
    return { hour: now.getHours(), minute: now.getMinutes(), second: now.getSeconds() };
  })();

  // ── Child input callbacks ────────────────────────────────────────────────

  const onDateTextChange = (v: string) => {
    setInternalError(null);
    setDateText(v);
  };

  const onTimeTextChange = (v: string) => {
    setInternalError(null);
    setTimeText(v);
  };

  const onDateCommit = () => {
    if (!dateText.trim()) pendingSeedRef.current.date = seedDate();
    commit();
  };

  const onTimeCommit = () => {
    if (!timeText.trim()) {
      const d =
        parseInputText(dateText) ??
        (pendingSeedRef.current.date ? parseInputText(pendingSeedRef.current.date) : null) ??
        undefined;
      pendingSeedRef.current.time = seedTime(d);
    }
    commit();
  };

  const onAdvanceToTime = hasTime
    ? () => {
        if (!dateText.trim()) pendingSeedRef.current.date = seedDate();
        timeInputRef.current?.focus();
      }
    : undefined;

  const onRetreatToDate = () => {
    const el = dateInputRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  };

  const onEnterSegmentMode = (fn: () => void) => {
    enterSegmentModeRef.current = fn;
  };

  const onOpenTimePicker = () => {
    if (!popoverOpen) {
      setPopoverMode('time');
      setPopoverOpen(true);
    }
  };

  const onArrowKeyInDate = (e: React.KeyboardEvent) => {
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !popoverOpen) {
      setPopoverMode('calendar');
      setPopoverOpen(true);
    }
  };

  const onEnterInDate = () => {
    closePopover();
  };

  const onContainerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      closePopover();
      // Restore focus to the relevant input
      if (popoverMode === 'time') {
        const el = timeInputRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }
      } else {
        const el = dateInputRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }
      }
    }
    if (e.key === 'Tab') {
      setPopoverOpen(false);
    }
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && popoverOpen && popoverMode === 'calendar') {
      e.preventDefault();
      const cal = popoverRef.current;
      if (cal) {
        const cell =
          cal.querySelector<HTMLElement>('[data-selected]') ??
          cal.querySelector<HTMLElement>('[data-today]') ??
          cal.querySelector<HTMLElement>('td[role="gridcell"] > span');
        if (cell) {
          cell.focus();
          cell.dispatchEvent(new KeyboardEvent('keydown', { key: e.key, bubbles: true }));
        }
      }
    }
  };

  const onWrapperClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) dateInputRef.current?.focus();
  };

  const onCalendarIconClick = () => {
    if (popoverOpen && popoverMode === 'calendar') {
      closePopover();
      dateInputRef.current?.focus();
    } else {
      dateInputRef.current?.focus();
    }
  };

  const onClockIconClick = () => {
    if (popoverOpen && popoverMode === 'time') {
      closePopover();
      timeInputRef.current?.focus();
    } else {
      timeInputRef.current?.focus();
    }
  };

  return {
    containerRef,
    dateInputRef,
    timeInputRef,
    popoverRef,
    dateText,
    timeText,
    isFocused,
    popoverOpen,
    popoverMode,
    internalError,
    hasTime,
    calendarValue,
    timePickerValue,
    timeConstraints,
    handleContainerFocus,
    handleContainerBlur,
    handleCalendarSelect,
    handleTimePickerChange,
    closePopover,
    commit,
    onDateTextChange,
    onTimeTextChange,
    onDateCommit,
    onTimeCommit,
    onAdvanceToTime,
    onRetreatToDate,
    onEnterSegmentMode,
    onOpenTimePicker,
    seedTime,
    onArrowKeyInDate,
    onEnterInDate,
    onContainerKeyDown,
    onWrapperClick,
    onCalendarIconClick,
    onClockIconClick,
  };
}
